import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowUp } from '../../../../../../../assets/svg/arrows/arrow-up-1.svg';
import { getOrderStatusOptions } from '../../../../../../../configs/orders';
import {
  getProductTypeLabels,
  getProductTypeOptions,
} from '../../../../../../../configs/product';
import {
  getServiceTypeLabels,
  getServiceTypeOptions,
} from '../../../../../../../configs/service';
import { Product } from '../../../../../../../enums/product';
import { Service } from '../../../../../../../enums/service';
import useAxiosHook, {
  UseAxiosReturn,
} from '../../../../../../../hooks/useAxiosHook';
import { ProductServiceCollection } from '../../../../../../../types/api/productServices';
import { queryString } from '../../../../../../../utils/helpers/http';
import { FormValues } from '../../CreateEditDialog.functions';
import styles from './Discounts.module.scss';
import EmptyContent from './EmptyContent';
import Row from './Row';
import TitleRow from './TitleRow';

type Props = {
  disabled: boolean;
};

function Discounts({ disabled }: Props): JSX.Element {
  const { t } = useTranslation();

  const {
    data: productServicesCollectionRequest,
    isLoading: isProductServicesLoading,
  } = useAxiosHook<UseAxiosReturn<ProductServiceCollection>>(
    '/product-services' +
      queryString({
        page: 1,
        limit: 0,
      })
  );

  const popust = useWatch<FormValues, 'popust'>({
    name: 'popust',
  });

  const { append } = useFieldArray<FormValues, 'popust'>({
    name: 'popust',
  });

  const productServiceMapping = useMemo<[Product, Service][]>(
    () =>
      productServicesCollectionRequest?.data?.data?.map((ps: any) => [
        ps.product_id,
        ps.service_id,
      ]) ?? [],
    [productServicesCollectionRequest?.data?.data]
  );

  const serviceLabels = useMemo(() => getServiceTypeLabels(t), [t]);
  const serviceOptions = useMemo(() => getServiceTypeOptions(t), [t]);

  const productLabels = useMemo(() => getProductTypeLabels(t), [t]);
  const productOptions = useMemo(() => getProductTypeOptions(t), [t]);

  const orderStatusOptions = useMemo(() => getOrderStatusOptions(t), [t]);

  if (!popust?.length && disabled) {
    return (
      <div>
        <ArrowUp className={styles.arrowUp} />

        <h3 className="p-mb-2">{t('Invoicing is not enabled')}</h3>

        {t('Invoicing must be enabled before discounts can be defined.')}
        <br />
        {t('Please make sure this option is checked in the Legal data tab.')}
      </div>
    );
  }

  return (
    <div>
      <Tooltip
        target=".discount-tooltip"
        position="top"
        style={!disabled ? { display: 'none' } : {}}
      />

      <div
        className="discount-tooltip"
        data-pr-tooltip={t(
          'Invoicing must be enabled. Please make sure this option is checked in the Legal data tab.'
        )}
      >
        {/* <FieldArray
          name="popust"
          render={(arrayHelpers) =>
            popust.length ? (
              <Content
                disabled={disabled}
                arrayHelpers={arrayHelpers}
                productServiceMapping={productServiceMapping}
                isLoading={productServicesCollectionRequest.isLoading}
              />
            ) : (
              )
              }
              /> */}
        {popust && popust.length > 0 ? (
          <div>
            <div className="p-field p-fluid">
              <div className={styles.discountsGrid}>
                <TitleRow />
                {popust?.map((_, index) => {
                  return (
                    <>
                      <Row
                        key={index}
                        idx={index}
                        serviceOptions={serviceOptions}
                        serviceLabels={serviceLabels}
                        productLabels={productLabels}
                        productOptions={productOptions}
                        orderStatusOptions={orderStatusOptions}
                        productServiceMapping={productServiceMapping}
                        disabled={disabled}
                        isLoading={isProductServicesLoading}
                      />
                    </>
                  );
                })}
              </div>
            </div>
            <Button
              type="button"
              label={t('Discount')}
              icon="fas fa-plus"
              className="p-button-outlined p-button-sm"
              onClick={() =>
                append({
                  UslugaId: 0,
                  ProizvodId: 0,
                  StatusId: 0,
                  Popust: '',
                })
              }
            />
          </div>
        ) : (
          <EmptyContent />
        )}
      </div>
    </div>
  );
}

export default Discounts;
